import { zodResolver } from "@hookform/resolvers/zod"
import { endOfWeek, format, parse, startOfWeek, subWeeks } from "date-fns"
import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import type { DateRange } from "design-system/molecules/DatePicker"
import { DateRangePicker } from "design-system/molecules/DatePicker"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { isFilled } from "lopermut"
import { CompanySelector } from "modules/backoffice/components/CompanySelector"
import { useBackofficeCampaignsQuery } from "modules/backoffice/hooks/campaigns/useBackofficeCampaignsQuery"
import { useBackofficeGoalsQuery } from "modules/backoffice/hooks/campaigns/useBackofficeGoalsQuery"
import { useGenerateClusterMutation } from "modules/backoffice/hooks/clusters/useGenerateClusterMutation"
import { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { BiasFilterEnum, ClusterDomainEnum, GenerateClusterFormSchema } from "schema"
import { z } from "zod"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultValues?: {
    companyId?: string
    domain?: (typeof ClusterDomainEnum)[keyof typeof ClusterDomainEnum]
    goalId?: string
    campaignId?: string
    from?: string
    to?: string
  }
}

export const GenerateClusterModal = ({ open, onOpenChange, defaultValues }: Props) => {
  const { t } = useTranslation()
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(
    defaultValues?.companyId,
  )

  const defaultWeekStart = useMemo(() => {
    const today = new Date()
    return startOfWeek(subWeeks(today, 2), { weekStartsOn: 1 })
  }, [])

  const form = useForm<z.infer<typeof GenerateClusterFormSchema>>({
    resolver: zodResolver(GenerateClusterFormSchema),
    defaultValues: {
      companyId: defaultValues?.companyId ?? "",
      from: defaultValues?.from ?? format(defaultWeekStart, "yyyy-MM-dd"),
      to:
        defaultValues?.to ?? format(endOfWeek(defaultWeekStart, { weekStartsOn: 1 }), "yyyy-MM-dd"),
      domain: defaultValues?.domain ?? ClusterDomainEnum.VoiceOfCustomer,
      goalId: defaultValues?.goalId,
      biasFilter: undefined,
      campaignId: undefined,
      epsilon: undefined,
      samplingFactor: undefined,
      selectionMethod: undefined,
      includeNoise: true,
    },
    mode: "onChange",
  })

  useEffect(() => {
    if (open && defaultValues) {
      const newDefaultWeekStart = defaultValues.from
        ? parse(defaultValues.from, "yyyy-MM-dd", new Date())
        : defaultWeekStart
      const newDefaultWeekEnd = defaultValues.to
        ? parse(defaultValues.to, "yyyy-MM-dd", new Date())
        : endOfWeek(newDefaultWeekStart, { weekStartsOn: 1 })

      form.reset({
        companyId: defaultValues.companyId ?? "",
        from: defaultValues.from ?? format(newDefaultWeekStart, "yyyy-MM-dd"),
        to: defaultValues.to ?? format(newDefaultWeekEnd, "yyyy-MM-dd"),
        domain: defaultValues.domain ?? ClusterDomainEnum.VoiceOfCustomer,
        goalId: defaultValues.goalId,
        biasFilter: undefined,
        campaignId: undefined,
        epsilon: undefined,
        samplingFactor: undefined,
        selectionMethod: undefined,
        includeNoise: true,
      })
      setSelectedCompanyId(defaultValues.companyId)
    }
  }, [open, defaultValues, form, defaultWeekStart])

  const selectedDomain = form.watch("domain")
  const selectedGoalId = form.watch("goalId")
  const fromValue = form.watch("from")
  const toValue = form.watch("to")
  const isGoalDomain = selectedDomain === ClusterDomainEnum.Goal

  const { data: goals } = useBackofficeGoalsQuery({
    companyId: selectedCompanyId ?? "",
    query: {
      page: 1,
      total: 100,
      sortBy: { field: "name", order: "asc" },
    },
    enabled: isGoalDomain && !!selectedCompanyId,
  })

  const { data: campaigns } = useBackofficeCampaignsQuery({
    companyId: selectedCompanyId ?? "",
    query: {
      page: 1,
      total: 100,
      sortBy: { field: "name", order: "asc" },
    },
    enabled: isGoalDomain && !!selectedCompanyId,
  })

  const goalsOptions = useMemo(() => {
    return (
      goals?.map((goal) => ({
        value: goal.id,
        label: goal.name,
      })) ?? []
    )
  }, [goals])

  const campaignsOptions = useMemo(() => {
    return (
      campaigns?.map((campaign) => ({
        value: campaign.id,
        label: campaign.name ?? "",
      })) ?? []
    )
  }, [campaigns])

  const selectedGoal = useMemo(() => {
    if (!selectedGoalId || !goals) return undefined
    return goals.find((goal) => goal.id === selectedGoalId)
  }, [selectedGoalId, goals])

  const showBiasFilter = isFilled(selectedGoal?.secondTurnBias)

  const domainOptions = useMemo(() => {
    return Object.values(ClusterDomainEnum).map((domain) => ({
      value: domain,
      label: t(`dashboard.domains.${domain}`),
    }))
  }, [t])

  const { mutateAsync, isPending } = useGenerateClusterMutation()

  const dateRange = useMemo<DateRange | undefined>(() => {
    if (!fromValue || !toValue) return undefined
    return {
      from: parse(fromValue, "yyyy-MM-dd", new Date()),
      to: parse(toValue, "yyyy-MM-dd", new Date()),
    }
  }, [fromValue, toValue])

  const handleCompanyChange = (companyId: string | undefined) => {
    setSelectedCompanyId(companyId)
    form.setValue("companyId", companyId ?? "")
    if (!isGoalDomain) {
      form.setValue("goalId", undefined)
      form.setValue("campaignId", undefined)
      form.setValue("biasFilter", undefined)
    }
  }

  const handleDomainChange = (domain: string) => {
    form.setValue("domain", domain as (typeof ClusterDomainEnum)[keyof typeof ClusterDomainEnum])
    if (domain !== ClusterDomainEnum.Goal) {
      form.setValue("goalId", undefined)
      form.setValue("campaignId", undefined)
      form.setValue("biasFilter", undefined)
    }
  }

  const handleGoalChange = (goalId: string | undefined) => {
    form.setValue("goalId", goalId)
    if (!goalId) {
      form.setValue("biasFilter", undefined)
    }
  }

  const onSubmit = async (data: z.infer<typeof GenerateClusterFormSchema>) => {
    await mutateAsync({
      companyId: data.companyId,
      from: data.from,
      to: data.to,
      domain: data.domain,
      goalId: data.goalId,
      campaignId: data.campaignId,
      epsilon: data.epsilon,
      samplingFactor: data.samplingFactor,
      selectionMethod: data.selectionMethod,
      includeNoise: data.includeNoise,
      biasFilter: data.biasFilter,
    })
    onOpenChange(false)
    form.reset()
    setSelectedCompanyId(undefined)
  }

  const handleDateRangeChange = (newDateRange: DateRange | undefined) => {
    if (newDateRange?.from && newDateRange?.to) {
      form.setValue("from", format(newDateRange.from, "yyyy-MM-dd"))
      form.setValue("to", format(newDateRange.to, "yyyy-MM-dd"))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("backoffice.clusters.generate.title")}</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            errorTranslations={getErrorTranslations()}
          >
            <FormField
              control={form.control}
              name="companyId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("backoffice.clusters.generate.company")}</FormLabel>
                  <FormControl>
                    <CompanySelector
                      value={selectedCompanyId}
                      onChange={(companyId) => {
                        handleCompanyChange(companyId)
                        field.onChange(companyId ?? "")
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="domain"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("backoffice.clusters.generate.domain")}</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={handleDomainChange}>
                      <SelectTrigger>
                        <SelectValue placeholder={t("backoffice.clusters.selectDomain")} />
                      </SelectTrigger>
                      <SelectContent>
                        {domainOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isGoalDomain && selectedCompanyId && (
              <FormField
                control={form.control}
                name="goalId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("backoffice.clusters.generate.goal")}</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          handleGoalChange(value)
                          field.onChange(value)
                        }}
                        disabled={!selectedCompanyId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t("backoffice.clusters.selectGoal")} />
                        </SelectTrigger>
                        <SelectContent>
                          {goalsOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {showBiasFilter && (
              <FormField
                control={form.control}
                name="biasFilter"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("backoffice.clusters.generate.biasFilter")}</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value ?? BiasFilterEnum.All}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t("backoffice.clusters.generate.biasFilter")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={BiasFilterEnum.All}>
                            {t("backoffice.clusters.generate.biasFilterAll")}
                          </SelectItem>
                          <SelectItem value={BiasFilterEnum.Biased}>
                            {t("backoffice.clusters.generate.biasFilterBiased")}
                          </SelectItem>
                          <SelectItem value={BiasFilterEnum.Unbiased}>
                            {t("backoffice.clusters.generate.biasFilterUnbiased")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {isGoalDomain && selectedCompanyId && (
              <FormField
                control={form.control}
                name="campaignId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("backoffice.clusters.generate.campaign")}</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={!selectedCompanyId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t("backoffice.clusters.selectCampaign")} />
                        </SelectTrigger>
                        <SelectContent>
                          {campaignsOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="from"
              render={() => (
                <FormItem>
                  <FormLabel>{t("backoffice.clusters.generate.dateRange")}</FormLabel>
                  <FormControl>
                    <DateRangePicker
                      dateRange={dateRange}
                      setDateRange={handleDateRangeChange}
                      placeholder={t("shared.dateRange.placeholder")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="epsilon"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Tooltip>
                      <TooltipTrigger slick>
                        {t("backoffice.clusters.generate.epsilon")}
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        {t("backoffice.clusters.generate.epsilonTooltip")}
                      </TooltipContent>
                    </Tooltip>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      inputMode="decimal"
                      value={field.value ?? ""}
                      onChange={(e) => {
                        const value = e.target.value
                        field.onChange(value === "" ? undefined : Number(value))
                      }}
                      placeholder={t("backoffice.clusters.generate.epsilonPlaceholder")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="samplingFactor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Tooltip>
                      <TooltipTrigger slick>
                        {t("backoffice.clusters.generate.samplingFactor")}
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        {t("backoffice.clusters.generate.samplingFactorTooltip")}
                      </TooltipContent>
                    </Tooltip>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      step="0.1"
                      value={field.value ?? ""}
                      onChange={(e) => {
                        const value = e.target.value
                        field.onChange(value === "" ? undefined : Number(value))
                      }}
                      placeholder={t("backoffice.clusters.generate.samplingFactorPlaceholder")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="selectionMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Tooltip>
                      <TooltipTrigger slick>
                        {t("backoffice.clusters.generate.selectionMethod")}
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        {t("backoffice.clusters.generate.selectionMethodTooltip")}
                      </TooltipContent>
                    </Tooltip>
                  </FormLabel>
                  <FormControl>
                    <Select
                      value={field.value ?? ""}
                      onValueChange={(value) => field.onChange(value === "" ? undefined : value)}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t("backoffice.clusters.generate.selectionMethodPlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="leaf">
                          {t("backoffice.clusters.generate.leaf")}
                        </SelectItem>
                        <SelectItem value="eom">{t("backoffice.clusters.generate.eom")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="includeNoise"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{t("backoffice.clusters.generate.includeNoise")}</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onOpenChange(false)
                  form.reset()
                  setSelectedCompanyId(undefined)
                }}
              >
                {t("shared.cancel")}
              </Button>
              <Button type="submit" disabled={isPending || !form.formState.isValid}>
                {t("backoffice.clusters.generate.submit")}
              </Button>
            </DialogFooter>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
